import React from 'react';
import { Box, Button } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { FormDrawer } from '../../../../components/form-drawer';
import { dataGridDefaults } from '../../../../components/list-grid-panel';
import { RowAction } from '../../../../components/row-action';
import { EditAction, DeleteAction } from '../../../../components/row-actions';
import PurchaseItemResultDrawer from './purchase-item-result-drawer';
import PurchaseItemForm from './purchase-item-form';
import PurchaseItemImportDialog from './purchase-item-import-dialog';
import PurchaseItemImportErrorsDialog from './purchase-item-import-errors-dialog';
import { usePurchaseItemController } from './controllers/purchase-item-controller';
import { IPurchaseItemResponse } from '../../../../model/scp/purchase/response/purchase-item-response';

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4, maximumFractionDigits: 4 });
const decimalFormatter = new Intl.NumberFormat('pt-BR', { style: 'decimal', minimumFractionDigits: 4, maximumFractionDigits: 4 });

export interface PurchaseItemDataTableProps {
    purchaseId?: string;
    modalityId?: number | null;
    itemsPurchaseJson?: string;
    setItemsPurchaseJson: (value: string) => void;
}

export default function PurchaseItemDataTable({ purchaseId, modalityId, itemsPurchaseJson, setItemsPurchaseJson }: PurchaseItemDataTableProps) {
    const ctrl = usePurchaseItemController(purchaseId, modalityId, setItemsPurchaseJson, itemsPurchaseJson);
    const isEditingPublished = !ctrl.isDraft && ctrl.itemPurchase.number !== undefined;

    const columns: GridColDef<IPurchaseItemResponse>[] = [
        { field: 'number', headerName: 'Nº', width: 80, sortable: false, disableColumnMenu: true, align: 'right', headerAlign: 'right' },
        { field: 'description', headerName: 'Descrição', flex: 2, minWidth: 180, sortable: false, disableColumnMenu: true },
        {
            field: 'quantity', headerName: 'Quantidade', flex: 1, minWidth: 130, sortable: false, disableColumnMenu: true,
            align: 'right', headerAlign: 'right', valueFormatter: ({ value }) => decimalFormatter.format(Number(value)),
        },
        { field: 'unitMeasure', headerName: 'Unidade de medida', flex: 1, minWidth: 150, sortable: false, disableColumnMenu: true },
        {
            field: 'unitValueEstimated', headerName: 'Valor estimado unitário', flex: 1, minWidth: 190, sortable: false, disableColumnMenu: true,
            align: 'right', headerAlign: 'right', valueFormatter: ({ value }) => currencyFormatter.format(Number(value)),
        },
        {
            field: 'amount', headerName: 'Valor total', flex: 1, minWidth: 150, sortable: false, disableColumnMenu: true,
            align: 'right', headerAlign: 'right', valueFormatter: ({ value }) => currencyFormatter.format(Number(value)),
        },
        {
            field: 'actions', headerName: 'Ações', width: 100, sortable: false, disableColumnMenu: true,
            renderCell: (params) => (
                <React.Fragment>
                    <EditAction onClick={() => ctrl.editItem(params.row)} />
                    {ctrl.isDraft ? (
                        <DeleteAction onClick={() => ctrl.deleteItem(params.row)} />
                    ) : (
                        <RowAction title="Resultado" onClick={() => params.row.number !== undefined && ctrl.openItemResultDrawer(params.row.number)}>
                            <AssessmentIcon fontSize="small" />
                        </RowAction>
                    )}
                </React.Fragment>
            )
        }
    ];

    // Rascunho pagina no navegador; itens publicados são paginados no servidor
    const paginationProps = ctrl.isDraft
        ? { initialState: { pagination: { paginationModel: { pageSize: 50, page: 0 } } } }
        : {
            paginationMode: 'server' as const,
            paginationModel: { page: ctrl.page, pageSize: ctrl.itemsPageSize },
            onPaginationModelChange: (model: { page: number }) => ctrl.handlePageChange(model.page),
            rowCount: ctrl.rowCount,
            pageSizeOptions: [ctrl.itemsPageSize],
        };

    return (
        <React.Fragment>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', my: 1.5 }}>
                {ctrl.isDraft && (
                    <React.Fragment>
                        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={ctrl.handleDownloadTemplate}>Baixar modelo</Button>
                        <Button variant="outlined" startIcon={<UploadIcon />} onClick={ctrl.openImportDialogHandler}>Importar planilha</Button>
                    </React.Fragment>
                )}
                <Button variant="contained" startIcon={<PlaylistAddIcon />} onClick={ctrl.addItem}>Adicionar item</Button>
            </Box>
            <DataGrid
                {...dataGridDefaults}
                columns={columns}
                rows={ctrl.items}
                loading={ctrl.loading}
                getRowId={(row: IPurchaseItemResponse) => row.number ?? 0}
                {...paginationProps}
            />

            <FormDrawer
                open={ctrl.openDrawer}
                icon={<ListAltIcon />}
                title={ctrl.itemPurchase.number !== undefined ? `Editar item nº ${ctrl.itemPurchase.number}` : 'Adicionar item'}
                saveLabel="Salvar item"
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeDrawer}
                onSave={ctrl.saveItem}
            >
                <PurchaseItemForm
                    itemPurchase={ctrl.itemPurchase}
                    materialService={ctrl.materialService}
                    benefitType={ctrl.benefitType}
                    judgementCriteria={ctrl.judgementCriteria}
                    categoryItemPurchase={ctrl.categoryItemPurchase}
                    purchaseItemSituation={ctrl.purchaseItemSituation}
                    showSituation={isEditingPublished}
                    errors={ctrl.validationErrors}
                    onChange={ctrl.handleOnChange}
                    onAmountChange={ctrl.handleAmountChange}
                />
            </FormDrawer>

            {ctrl.resultItemNumber !== null && purchaseId && (
                <PurchaseItemResultDrawer
                    purchaseId={Number(purchaseId)}
                    itemNumber={ctrl.resultItemNumber}
                    resultData={ctrl.itemResult}
                    onClose={ctrl.closeItemResultDrawer}
                    onSaved={ctrl.reloadItems}
                />
            )}

            <PurchaseItemImportDialog open={ctrl.openImportDialog} onClose={ctrl.closeImportDialog} onImport={ctrl.handleUploadFile} />
            <PurchaseItemImportErrorsDialog
                open={ctrl.importErrors.length > 0}
                onClose={ctrl.closeImportErrorsDialog}
                errors={ctrl.importErrors}
                successCount={ctrl.importSuccessCount}
                totalCount={ctrl.importTotalCount}
            />
        </React.Fragment>
    );
}
